/**
 * WaypointExtractor - Extracts waypoint data from URP XML documents
 * 
 * Responsible for:
 * - Extracting all waypoints from parsed XML
 * - Parsing individual waypoint elements
 * - Extracting via-points for circular arcs
 * - Extracting joint positions
 */
export class WaypointExtractor {
  /**
   * Extract all waypoints from the XML document
   * @param {Document} xmlDoc - The parsed XML document
   * @returns {Array<Object>} Array of waypoint objects
   */
  extractWaypoints(xmlDoc) {
    if (!xmlDoc) return [];
    
    const waypoints = [];
    
    // Find all Pose elements with key="way-point"
    const poses = xmlDoc.querySelectorAll('Pose[key="way-point"]');
    
    let waypointCounter = 1;
    poses.forEach((pose, index) => {
      const waypoint = this.parseWaypoint(pose, index, waypointCounter);
      if (waypoint) {
        waypoints.push(waypoint);
        waypointCounter++;
      }
    });
    
    return waypoints;
  }

  /**
   * Parse a single waypoint element
   * @param {Element} poseElement - The Pose XML element
   * @param {number} index - Index of the waypoint
   * @param {number} displayNumber - The display number (1-based continuous counter)
   * @returns {Object|null} Waypoint data or null if parsing fails
   */
  parseWaypoint(poseElement, index, displayNumber = index + 1) {
    try {
      // Get parent element to extract additional metadata.
      // 'suppressedNode' is included because a node disabled in PolyScope keeps
      // an identical dataModel under that tag; matching only 'Contributed' makes
      // its waypoints report feature 'N/A' and drop out of feature operations.
      const parent = poseElement.closest('Contributed, suppressedNode');
      const processType = parent?.querySelector('data[key="process-type"]')?.getAttribute('value');
      const feature = parent?.querySelector('data[key="feature"]')?.getAttribute('value');
      const motionType = parent?.querySelector('data[key="motion-type"]')?.getAttribute('value');
      
      // Get node type (WeldNode, AirMoveNode, etc.)
      const nodeType = parent?.getAttribute('strategyProgramNodeType') || 'Unknown';
      const strategyClass = parent?.getAttribute('strategyClass') || '';
      
      // Find the parent node (WeldNode or AirMoveNode container)
      let parentNode = parent;
      if (processType) {
        parentNode = parent.parentElement?.closest('Contributed[strategyProgramNodeType="Weld"]') || parent;
      }

      // A suppressed node is still a real node; surface that rather than
      // silently presenting it as an ordinary waypoint.
      const isSuppressed = parent?.tagName === 'suppressedNode';
      
      // Extract via-point data
      const viaPointData = this.extractViaPoint(parent, motionType);
      
      // Extract position
      const position = this.extractPosition(poseElement);
      
      // Extract rotation
      const rotation = this.extractRotation(poseElement);
      
      // Extract joint positions
      const jointPositions = this.extractJointPositions(parent);
      
      return {
        index: index,
        number: displayNumber.toString(),
        processType: processType || nodeType,
        feature: feature || 'N/A',
        nodeType: nodeType,
        strategyClass: strategyClass,
        parentNode: parentNode,
        motionType: motionType || 'L',
        isSuppressed: isSuppressed,
        isCircular: viaPointData.isCircular,
        viaPoint: viaPointData.viaPoint,
        position: position,
        rotation: rotation,
        jointPositions: jointPositions,
        element: poseElement
      };
      
    } catch (error) {
      console.warn('Failed to parse waypoint:', error);
      return null;
    }
  }

  /**
   * Extract via-point data for circular arcs
   * @param {Element} parent - Parent Contributed element
   * @param {string} motionType - Motion type (L or C)
   * @returns {{isCircular: boolean, viaPoint: Object|null}}
   */
  extractViaPoint(parent, motionType) {
    const viaPointElement = parent?.querySelector('Pose[key="via-point"]');
    const isCircular = viaPointElement !== null || motionType === 'C';
    let viaPoint = null;
    
    if (viaPointElement) {
      const viaPosition = this.extractPosition(viaPointElement);
      const viaRotation = this.extractRotation(viaPointElement);
      
      viaPoint = {
        position: viaPosition,
        rotation: viaRotation,
        element: viaPointElement
      };
    }
    
    return { isCircular, viaPoint };
  }

  /**
   * Extract position from a Pose element
   * @param {Element} poseElement - Pose element
   * @returns {{x: number, y: number, z: number}}
   */
  extractPosition(poseElement) {
    const position = poseElement.querySelector('Position[key="posePosition"]');
    return {
      x: parseFloat(position.querySelector('Length[key="X"]').getAttribute('value')),
      y: parseFloat(position.querySelector('Length[key="Y"]').getAttribute('value')),
      z: parseFloat(position.querySelector('Length[key="Z"]').getAttribute('value'))
    };
  }

  /**
   * Extract rotation from a Pose element
   * @param {Element} poseElement - Pose element
   * @returns {{rx: number, ry: number, rz: number}}
   */
  extractRotation(poseElement) {
    const rotation = poseElement.querySelector('Rotation[key="poseRotation"]');
    return {
      rx: parseFloat(rotation.querySelector('Angle[key="RX"]').getAttribute('value')),
      ry: parseFloat(rotation.querySelector('Angle[key="RY"]').getAttribute('value')),
      rz: parseFloat(rotation.querySelector('Angle[key="RZ"]').getAttribute('value'))
    };
  }

  /**
   * Extract joint positions from a Contributed element
   * @param {Element} parent - Parent Contributed element
   * @returns {Array<number>|null} Array of 6 joint angles or null
   */
  extractJointPositions(parent) {
    const jointPositionsElement = parent?.querySelector('JointPositions[key="way-point-joint"]');
    if (!jointPositionsElement) return null;
    
    const joints = [];
    for (let i = 1; i <= 6; i++) {
      const jointElement = jointPositionsElement.querySelector(`JointPosition[key="[${i}]"]`);
      if (jointElement) {
        const angleElement = jointElement.querySelector('Angle[key="pos"]');
        if (angleElement) {
          joints.push(parseFloat(angleElement.getAttribute('value')));
        }
      }
    }
    
    return joints.length === 6 ? joints : null;
  }
}
